import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

/**
 * AuthGuard
 *
 * Lightweight presence check: if there's no authenticated user or no token,
 * send the visitor to the login screen. Nothing more.
 *
 * Token *validity* (expiry) is intentionally NOT checked here. That is owned by
 * the global 401 handler in services/api.js, which — on the first real API call
 * the page makes — silently refreshes an expired access token when possible, and
 * otherwise clears tokens, sets the `faithinsight_session_expired` flag, and
 * redirects to /login with the "session expired" banner.
 *
 * Previously this component made its own validateToken() network call and cleared
 * tokens on 401/403. Because AuthGuard only wraps a subset of routes, that produced
 * inconsistent behavior: a redundant round-trip (the "Validating authentication…"
 * spinner) on some pages but not others, and a clear path that did NOT set the
 * session-expired flag — so those routes could bounce to /login without the banner.
 * Deferring to the single global handler makes every route behave identically.
 */
const AuthGuard = ({ children }) => {
  const { user } = useAuth();
  const token = localStorage.getItem('faithinsight_token');

  if (!user || !token) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

export default AuthGuard;
